import { useState } from 'react';
import { supabase } from '../lib/supabase';

// 30s ceiling for the TAM Edge Function. Tunable: watch p95 latency post-launch
// and bump to ~45s if real-world edge cases (large competitor sets, dense
// markets) regularly run >25s.
const TAM_TIMEOUT_MS = 30000;

export interface TamEstimationInput {
  // Option 1: Provide station_object_id for server-side data lookup
  station_object_id?: number;

  // Option 2: Provide all characteristic data directly
  fuel_brand?: string | null;
  store_brand?: string | null;
  county?: string | null;
  state?: string | null;
  pop1?: number | null;
  adt?: number | null;
  dispensers?: number | null;
  store_size?: number | null;
  exposure?: number | null;
}

export interface TamEstimationResult {
  subject: TamEstimationInput;
  estimates: {
    fuel_volume_gal: number;
    fuel_profit_margin_per_gal: number;
    cstore_sales: number;
    cstore_margin_pct: number;
    opex_over_gp_pct: number;
    carwash_sales?: number; // NEW: Optional car wash annual sales
    carwash_margin_pct?: number; // NEW: Optional car wash profit margin %
  };
  diagnostics: {
    legs: {
      traffic_leg: number | null;
      pop_leg: number | null;
      capacity_leg: number | null;
      weights_used: {
        traffic: number;
        pop: number;
        capacity: number;
      };
    };
    factors: {
      brand: {
        factor: number;
        n: number;
      };
      geography: {
        factor: number;
        n: number;
      };
      pre_factor_fuel_tam: number;
    };
    cohort: {
      tier: string;
      n: number;
    };
    benchmarks: {
      year: number;
      fuel_margin_national: number;
      cstore_margin_national: number;
      opex_gp_national: number;
      ratio_cstore_per_gal: number;
    };
    cstore_link: {
      tier: string;
      n: number;
      r2: number | null;
      method: string;
      slope: number | null;
      intercept: number | null;
      median_ratio: number | null;
      cstore_from_fuel: number | null;
      cstore_from_size: number | null;
      cstore_from_benchmark_ratio: number;
      blend_weights: {
        fuel: number;
        size: number;
        bench: number;
      };
    };
  };
}

export function useTamEstimation() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TamEstimationResult | null>(null);

  const fetchTamEstimation = async (input: TamEstimationInput) => {
    setIsLoading(true);
    setError(null);

    console.log('=== Calling TAM Edge Function ===');
    console.log('Input payload:', input);

    try {
      const invokePromise = supabase.client.functions.invoke('tam-simple', {
        body: input
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new Error(
            "Unable to generate projections: The TAM service didn't respond in time. " +
            "Please try again or enter values manually."
          )),
          TAM_TIMEOUT_MS
        );
      });

      const { data, error: functionError } = await Promise.race([
        invokePromise,
        timeoutPromise,
      ]) as Awaited<typeof invokePromise>;

      console.log('Edge Function response:', { data, error: functionError });

      if (functionError) {
        console.error('Edge Function error:', functionError);
        // supabase-js wraps non-2xx responses in FunctionsHttpError with a generic
        // message and stashes the actual Response on `.context`. Parse the body so
        // the user sees the friendly error the function returned, not the wrapper.
        let bodyMessage = '';
        const ctx = (functionError as any).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body && typeof body.error === 'string') {
              bodyMessage = body.error;
            }
          } catch {
            // Body wasn't JSON — fall through to the wrapper message.
          }
        }
        throw new Error(bodyMessage || functionError.message || 'TAM service error');
      }

      // Check if data contains an error
      if (data && data.error) {
        console.error('Edge Function returned error in data:', data.error);
        throw new Error(data.error);
      }

      console.log('TAM estimation successful:', data);
      setResult(data as TamEstimationResult);
      return data as TamEstimationResult;
    } catch (err: any) {
      const errorMessage = err?.message || err?.error || 'Failed to fetch TAM estimation';
      // Clean up the error message - remove technical prefixes
      const cleanMessage = errorMessage
        .replace(/^Error:\s*/i, '')
        .replace(/^TAM Estimation Error:\s*/i, '');
      setError(cleanMessage);
      console.error('=== TAM estimation error ===');
      console.error('Error object:', err);
      console.error('Error message:', cleanMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    result,
    fetchTamEstimation
  };
}
